import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Hint, SegmentedControl, Skeleton, cn } from '@wit-support/ui';
import { dayLabel } from '../period.js';
import { useMomentReports, useMomentsRetry } from '../queries.js';
import { accuracyOf, bucketsMissing, factCheckOf, firstFactFrom, hasMatureFact, ladderSummary, ladderTotal, mapSummary, momentWeeks, nextMaturingWeek, readLadderSort, readMomentsHorizon, readMomentsView, DEFAULT_MOMENTS_HORIZON, DEFAULT_MOMENTS_VIEW, } from '../moments-logic.js';
import { stepWords } from '../step-words.js';
import { FORECAST_HORIZONS } from '../trend-logic.js';
import { linkTo } from '../url-state.js';
import { ErrorBlock } from './error-block.js';
import { MomentsAccuracy } from './moments-accuracy.js';
import { MomentsLadder } from './moments-ladder.js';
import { MomentsMap } from './moments-map.js';
/**
 * «Как уточнялся прогноз» (Динамика): одни и те же корзины шага экрана (дни,
 * недели или месяцы) в трёх прогнозах — при покупке, через сутки, на 14-й
 * день. Три вида: «Лесенка» (по умолчанию), «Карта», «Точность». На шаге
 * «Дни» три отчёта запрашиваются с дневной разбивкой (`includeDays`, окно не
 * длиннее 92 дней — его обрезает экран); недели и месяцы — один и тот же
 * ответ без дней.
 *
 * Три отчёта (`moment=t0|t24|d14`) тяжёлые, а admin-api пускает к auto-ads не
 * больше трёх запросов сразу на всех пользователей — поэтому блок:
 * - грузит лениво: когда он реально попал в видимую область (без запаса) или
 *   пользователь нажал «Показать»; это защёлка — обратно в заглушку блок не
 *   превращается, наблюдатель после первого попадания отключается;
 * - грузит по одному (`useMomentReports`): сверх основного отчёта экрана в
 *   полёте не больше одного запроса блока;
 * - готовность основного отчёта тоже защёлкивается на ключ (период,
 *   кабинеты, модель): её мигание не останавливает очередь.
 * Кэш — общий с «О прогнозе» и панелью кампании (`reportKey`): если в адресе
 * уже выбран один из этих моментов, его отчёт не запрашивается второй раз.
 * Ошибка (в том числе 503/504) не повторяется сама — ни при новом попадании
 * в видимую область, ни при смене готовности: только кнопкой «Повторить», и
 * только упавшие отчёты, по одному (`useMomentsRetry`). Пока очередь или
 * повтор идут, кнопка неактивна. Ответ про другой момент — тоже ошибка
 * («неожиданный ответ»), а не вечное «обновляется».
 *
 * Недели сводятся, только когда все три ответа относятся к текущему ключу: при
 * смене периода прошлые ответы (`keepPreviousData`) не склеиваются с новыми —
 * пока так, показан скелетон «обновляется».
 *
 * Адрес: `mv` — вид, `mh` — срок (60/90/120, по умолчанию 120; свой, не
 * общий с «Сбывается ли прогноз»: там 60 по умолчанию и неактивны сроки без
 * факта), `ms` — порядок «Лесенки», `mx=1` — только перешедшие порог,
 * `mf=hide` — скрыть недели меньше 30 покупок.
 */
const VIEWS = [
    { value: 'ladder', label: 'Лесенка' },
    { value: 'map', label: 'Карта' },
    { value: 'accuracy', label: 'Точность' },
];
/**
 * Блок хоть раз реально попал в видимую область (без запаса). Защёлка: после
 * первого попадания наблюдатель отключается и `true` не сбрасывается. Без
 * IntersectionObserver — только кнопка «Показать».
 */
function useSeen(ref) {
    const [seen, setSeen] = useState(false);
    useEffect(() => {
        if (seen)
            return;
        const el = ref.current;
        const IO = globalThis
            .IntersectionObserver;
        if (!el || !IO)
            return;
        const io = new IO((entries) => {
            if (!entries.some((e) => e.isIntersecting))
                return;
            io.disconnect();
            setSeen(true);
        }, { rootMargin: '0px' });
        io.observe(el);
        return () => io.disconnect();
    }, [ref, seen]);
    return seen;
}
/**
 * Ключ блока: период, кабинеты, модель и нужны ли дни — у ответов и у защёлки
 * готовности (основной отчёт экрана на «Днях» — другой запрос).
 */
const blockKey = (q, includeDays) => JSON.stringify([q.from, q.to, [...q.accounts].sort(), q.model ?? null, includeDays]);
/** Нет дневной разбивки в отчётах этапов (старый auto-ads). */
export const MOMENT_DAYS_MISSING_TEXT = 'Разбивка по дням для трёх прогнозов появится после обновления auto-ads. Пока смотрите по неделям.';
const HORIZONS_DESC = [...FORECAST_HORIZONS].reverse();
export function MomentsBlock({ step, query, ready, theta, today, search, setSearch, }) {
    const sw = stepWords(step);
    const includeDays = step === 'day';
    const ref = useRef(null);
    const seen = useSeen(ref);
    const [opened, setOpened] = useState(false);
    const active = seen || opened;
    // Готовность основного отчёта — защёлка на ключ: мигание `ready` (фоновая
    // перезагрузка отчёта экрана) не останавливает очередь блока.
    const key = blockKey(query, includeDays);
    const [readyKey, setReadyKey] = useState(null);
    useEffect(() => {
        if (ready)
            setReadyKey(key);
    }, [ready, key]);
    const go = active && (ready || readyKey === key);
    const reports = useMomentReports({ from: query.from, to: query.to, accounts: query.accounts, model: query.model, includeDays }, { enabled: go });
    const [r0, r24, r14] = reports;
    // Все три ответа — этого ключа и не заглушки прошлого: иначе новый t0
    // склеился бы со старыми t24/d14.
    const current = reports.every((r) => r.isCurrent);
    const updating = !current && reports.some((r) => r.data !== undefined);
    const d0 = current ? r0?.data : undefined;
    const d24 = current ? r24?.data : undefined;
    const d14 = current ? r14?.data : undefined;
    const view = readMomentsView(search['mv']);
    const mh = readMomentsHorizon(search['mh']);
    const sort = readLadderSort(search['ms']);
    const onlyCrossed = search['mx'] === '1';
    const hideFew = search['mf'] === 'hide';
    const daysMissing = !!d0 && !!d24 && !!d14 && bucketsMissing({ t0: d0, t24: d24, d14 }, step);
    const byHorizon = useMemo(() => {
        if (!d0 || !d24 || !d14)
            return null;
        const R = { t0: d0, t24: d24, d14: d14 };
        return Object.fromEntries(FORECAST_HORIZONS.map((h) => [h, momentWeeks(R, h, theta, step)]));
    }, [d0, d24, d14, theta, step]);
    const failed = reports.filter((r) => r.isError);
    // Только упавшие и по одному; не параллельно очереди и не дважды.
    const { retry, busy } = useMomentsRetry(reports);
    const shownAt = byHorizon
        ? Math.min(...reports.map((r) => r.dataUpdatedAt).filter((t) => t > 0))
        : undefined;
    const mature = byHorizon
        ? Object.fromEntries(FORECAST_HORIZONS.map((h) => [h, hasMatureFact(byHorizon[h])]))
        : null;
    // «Точность» без факта за выбранный срок показывает самый длинный срок с фактом.
    const h = view === 'accuracy' && mature && !mature[mh]
        ? (HORIZONS_DESC.find((x) => mature[x]) ?? mh)
        : mh;
    const set = (patch) => setSearch(patch);
    const setView = (v) => set({ mv: v === DEFAULT_MOMENTS_VIEW ? undefined : v });
    const setHorizon = (x) => set({ mh: x === DEFAULT_MOMENTS_HORIZON ? undefined : x });
    const linkFor = (w) => linkTo('campaigns', { from: w.from, to: w.to }, search);
    const horizonButtons = (_jsx("div", { role: "group", "aria-label": "\u0421\u0440\u043E\u043A", className: "flex gap-1", children: FORECAST_HORIZONS.map((x) => {
            const noFact = mature ? !mature[x] : false;
            const from = noFact && byHorizon ? firstFactFrom(byHorizon[x]) : null;
            const disabled = view === 'accuracy' && noFact;
            const btn = (_jsxs(Button, { type: "button", size: "sm", variant: x === h ? 'primary' : 'secondary', "aria-pressed": x === h, disabled: disabled, onClick: () => setHorizon(x), children: [x, " \u0434\u043D", disabled && from && from > today ? ` · с ${dayLabel(from)}` : ''] }, x));
            return noFact && !disabled && from && from > today ? (_jsx(Hint, { content: `Факт за ${x} дней появится примерно с ${dayLabel(from)}.`, children: btn }, x)) : (btn);
        }) }));
    let body;
    if (!active) {
        body = (_jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2", "data-testid": "moments-placeholder", children: [_jsxs("span", { className: "text-xs text-[var(--color-text-muted)]", children: ["\u0422\u0440\u0438 \u043F\u0440\u043E\u0433\u043D\u043E\u0437\u0430 ", sw.byPl, " \u2014 \u043F\u0440\u0438 \u043F\u043E\u043A\u0443\u043F\u043A\u0435, \u0447\u0435\u0440\u0435\u0437 \u0441\u0443\u0442\u043A\u0438 \u0438 \u043D\u0430 14-\u0439 \u0434\u0435\u043D\u044C \u2014 \u0437\u0430\u0433\u0440\u0443\u0437\u044F\u0442\u0441\u044F, \u043A\u043E\u0433\u0434\u0430 \u0431\u043B\u043E\u043A \u043F\u043E\u044F\u0432\u0438\u0442\u0441\u044F \u043D\u0430 \u044D\u043A\u0440\u0430\u043D\u0435."] }), _jsx(Button, { type: "button", size: "sm", variant: "secondary", onClick: () => setOpened(true), children: "\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u044C" })] }));
    }
    else if (failed.length > 0 && !byHorizon) {
        body = _jsx(ErrorBlock, { error: failed[0].error, onRetry: retry, retryDisabled: busy });
    }
    else if (!byHorizon && updating) {
        body = (_jsxs("div", { className: "flex flex-col gap-1.5", "data-testid": "moments-updating", "aria-busy": "true", children: [_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u041E\u0431\u043D\u043E\u0432\u043B\u044F\u0435\u0442\u0441\u044F: \u043F\u0440\u043E\u0433\u043D\u043E\u0437\u044B \u0437\u0430\u0433\u0440\u0443\u0436\u0430\u044E\u0442\u0441\u044F \u043F\u043E \u043E\u0434\u043D\u043E\u043C\u0443\u2026" }), _jsx(Skeleton, { className: "h-[360px] w-full" })] }));
    }
    else if (!byHorizon) {
        body = _jsx(Skeleton, { className: "h-[360px] w-full", "data-testid": "moments-loading" });
    }
    else if (daysMissing) {
        body = (_jsx("p", { className: "text-sm text-[var(--color-text-muted)]", "data-testid": "moments-days-missing", children: MOMENT_DAYS_MISSING_TEXT }));
    }
    else {
        const weeks = byHorizon[h];
        const total = ladderTotal(weeks);
        const check = factCheckOf(byHorizon, h);
        const summary = ladderSummary(weeks, total, check, step);
        const shorterFact = (w) => {
            for (const x of HORIZONS_DESC) {
                if (Number(x) >= Number(h))
                    continue;
                const sw = byHorizon[x].find((y) => y.from === w.from);
                if (sw && sw.fact.state !== 'none')
                    return { h: x, week: sw };
            }
            return null;
        };
        body = (_jsxs(_Fragment, { children: [failed.length > 0 ? (_jsx(ErrorBlock, { error: failed[0].error, onRetry: retry, retryDisabled: busy, shownAt: shownAt })) : null, weeks.length === 0 ? (_jsxs("p", { className: "text-sm text-[var(--color-text-muted)]", children: ["\u041D\u0435\u0442 ", sw.genPl, " \u0441 \u043F\u043E\u043A\u0443\u043F\u043A\u0430\u043C\u0438."] })) : view === 'ladder' ? (_jsx(MomentsLadder, { step: step, weeks: weeks, total: total, summary: summary, theta: theta, h: h, today: today, sort: sort, onSort: (s) => set({ ms: s === 'week' ? undefined : s }), onlyCrossed: onlyCrossed, onOnlyCrossed: (v) => set({ mx: v ? '1' : undefined }), hideFew: hideFew, onHideFew: (v) => set({ mf: v ? 'hide' : undefined }), linkFor: linkFor, shorterFact: shorterFact })) : view === 'map' ? (_jsx(MomentsMap, { step: step, byHorizon: byHorizon, h: h, theta: theta, today: today, summary: mapSummary((h === '60' ? ['60'] : ['60', h]).map((x) => ({
                        h: x,
                        weeks: byHorizon[x],
                    })), theta, step), crossNote: [summary.cross, summary.warn].filter(Boolean).join(' '), linkFor: linkFor })) : (_jsx(MomentsAccuracy, { step: step, a: accuracyOf(byHorizon[h]), h: h, weeks: byHorizon[h], today: today, fallbackFrom: h !== mh ? { h: mh, readyFrom: firstFactFrom(byHorizon[mh]) } : null, later: FORECAST_HORIZONS.filter((x) => mature && !mature[x]).map((x) => ({
                        h: x,
                        readyFrom: firstFactFrom(byHorizon[x]),
                    })), next: nextMaturingWeek(byHorizon[h], today) }))] }));
    }
    return (_jsx("div", { ref: ref, className: "min-w-0", children: _jsxs(Card, { className: "flex min-w-0 flex-col gap-2.5 px-[18px] py-3.5", "aria-label": "\u041A\u0430\u043A \u0443\u0442\u043E\u0447\u043D\u044F\u043B\u0441\u044F \u043F\u0440\u043E\u0433\u043D\u043E\u0437", "data-testid": "moments-block", children: [_jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2", children: [_jsxs("b", { className: "text-[15px]", children: ["\u041A\u0430\u043A \u0443\u0442\u043E\u0447\u043D\u044F\u043B\u0441\u044F \u043F\u0440\u043E\u0433\u043D\u043E\u0437", _jsxs("span", { className: cn('hidden font-normal sm:inline'), children: [' ', "\u00B7 \u0441\u043A\u043E\u043B\u044C\u043A\u043E \u0432\u0435\u0440\u043D\u0451\u0442\u0441\u044F \u043D\u0430 1 $ \u0437\u0430 ", h, " \u0434\u043D\u0435\u0439"] })] }), _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx(SegmentedControl, { label: "\u0412\u0438\u0434", value: view, onChange: setView, options: VIEWS }), horizonButtons] })] }), body] }) }));
}
//# sourceMappingURL=moments-block.js.map